// Follow-up row — conversations where we're waiting on ourselves.
// A thread marked in_progress whose last message is outbound and older than 2 days.
//
// Polish pass #6 (Circle Back redesign):
//   - Title leads with the Haiku-written ask (aiSummary) so Kimi can read
//     the row and know "what does this person want" without opening it.
//     Italicized highlight substring mirrors how DecideCard renders.
//   - Secondary line is a compact meta string ("Last touched Xd ago"), not
//     a duplicate of the title.
//   - Customer initial avatar on the left gives each row a human anchor.
//   - Channel-colored left-border accent (ch-gmail = clay, ch-shop =
//     monstera, ch-etsy = amber, ch-amzn = navy) so the channel registers
//     peripherally — channel + brand pills still ride inside the row.

import { Tag } from '@/components/brand/Tag';
import type { TagVariant } from '@/components/brand/Tag';
import type { Brand, Channel } from '@md/shared';
import type { AgeState } from '@/lib/display';

export type FollowupRowData = {
  id: string;
  customerName: string;
  brand: Brand;
  channel: Channel;
  daysWaiting: number;
  /**
   * Coarse age bucket for the #22 color-coding dot. Followups are always
   * >=2 days old (by construction), so in practice this is 'aging' or 'stale'
   * but we still pipe it through so the dot colors match DecideCard logic
   * exactly.
   */
  ageState?: AgeState;
  /** Row title — Haiku ask when available, raw subject as fallback. */
  subject: string;
  /** Secondary meta line below the title, e.g. "Last touched 4d ago". */
  summary: string;
  /**
   * Polish #6. Raw aiSummary + highlight — used to italicize the 1-5 word
   * emphasized phrase inside the title. Null when no AI summary is cached
   * yet; we then fall back to plain text rendering of `subject`.
   */
  aiSummary?: string | null;
  aiHighlight?: string | null;
  /**
   * Phase 9. Detected intent chip — null when classifier returned 'generic'
   * so we don't clutter the row.
   */
  intentChip?: {
    intent: string;
    label: string;
    chipClass: string;
    evidence: string | null;
  } | null;
};

type FollowupRowProps = {
  data: FollowupRowData;
  onOpen: (id: string) => void;
};

// 'shipstation' is auth-storage only — never a conversation channel in practice.
const CHANNEL_LABEL: Record<Channel, string> = {
  gmail: 'Gmail',
  etsy_md: 'Etsy',
  etsy_hk: 'Etsy',
  amazon: 'Amazon',
  shopify_md: 'Shopify',
  shopify_brokerage: 'Shopify',
  shipstation: 'ShipStation',
};

const CHANNEL_TAG: Record<Channel, TagVariant> = {
  gmail: 'ch-gmail',
  etsy_md: 'ch-etsy',
  etsy_hk: 'ch-etsy',
  amazon: 'ch-amzn',
  shopify_md: 'ch-shop',
  shopify_brokerage: 'ch-shop',
  shipstation: 'ch-shop',
};

// Accent class suffix driving `.followup-row--ch-*` left-border color in CSS.
const CHANNEL_ACCENT: Record<Channel, string> = {
  gmail: 'gmail',
  etsy_md: 'etsy',
  etsy_hk: 'etsy',
  amazon: 'amzn',
  shopify_md: 'shop',
  shopify_brokerage: 'shop',
  shipstation: 'shop',
};

// Human-readable tooltip text for the colored age dot. Mirrors the #22 scope:
// green <24h, yellow 1–3d, red >3d unreplied.
const AGE_DOT_TITLE: Record<AgeState, string> = {
  fresh: 'Under 24 hours old',
  aging: '1 to 3 days old',
  stale: 'Over 3 days old',
};

/** First letter of the customer's display name, uppercased. Falls back to "?". */
function initialOf(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return '?';
  const first = trimmed[0];
  return first ? first.toUpperCase() : '?';
}

/**
 * Render the title — if we have an aiSummary + highlight pair, italicize the
 * highlight substring; otherwise plain text. Mirrors DecideCard's HeroHeadline.
 */
function TitleLine({
  subject,
  aiSummary,
  aiHighlight,
}: {
  subject: string;
  aiSummary: string | null | undefined;
  aiHighlight: string | null | undefined;
}) {
  if (aiSummary && aiSummary.trim().length > 0) {
    const highlight = aiHighlight && aiSummary.includes(aiHighlight) ? aiHighlight : null;
    if (!highlight) {
      return <strong>{aiSummary}</strong>;
    }
    const i = aiSummary.indexOf(highlight);
    const before = aiSummary.slice(0, i);
    const after = aiSummary.slice(i + highlight.length);
    return (
      <strong>
        {before}
        <em>{highlight}</em>
        {after}
      </strong>
    );
  }
  return <strong>{subject.trim().length > 0 ? subject : '(no subject)'}</strong>;
}

export function FollowupRow({ data, onOpen }: FollowupRowProps) {
  const {
    id,
    customerName,
    brand,
    channel,
    daysWaiting,
    ageState,
    subject,
    summary,
    aiSummary,
    aiHighlight,
    intentChip,
  } = data;
  const accent = CHANNEL_ACCENT[channel];

  return (
    <div className={`followup-row followup-row--ch-${accent}`}>
      <div className={`followup-avatar followup-avatar--ch-${accent}`} aria-hidden>
        {initialOf(customerName)}
      </div>
      <div className="followup-body">
        <div className="followup-title">
          <TitleLine subject={subject} aiSummary={aiSummary} aiHighlight={aiHighlight} />
        </div>
        <div className="followup-meta">
          {ageState ? (
            <span
              className={`age-dot age-dot--${ageState}`}
              aria-hidden
              title={AGE_DOT_TITLE[ageState]}
              style={{ marginRight: 2, verticalAlign: 'middle' }}
            />
          ) : null}
          <span className="followup-meta-days">
            {summary}
            {daysWaiting >= 4 ? ' · ' : null}
            {daysWaiting >= 4 ? <em>overdue</em> : null}
          </span>
          <span className="followup-meta-sep" aria-hidden>·</span>
          <span className="followup-meta-who">{customerName}</span>
        </div>
        <div className="followup-tags">
          <Tag variant={brand === 'md' ? 'brand-md' : 'brand-hk'}>
            {brand === 'md' ? 'MD' : 'HK'}
          </Tag>
          <Tag variant={CHANNEL_TAG[channel]}>{CHANNEL_LABEL[channel]}</Tag>
          {intentChip ? (
            <span
              className={`intent-chip ${intentChip.chipClass}`}
              title={intentChip.evidence ?? intentChip.label}
            >
              {intentChip.label}
            </span>
          ) : null}
        </div>
      </div>
      <button type="button" className="btn sm" onClick={() => onOpen(id)}>
        Reply now
      </button>
    </div>
  );
}
